import { ReactNode, useEffect, useMemo, useState } from "react";
import { Optional } from "../../../types";
import { AutocompleteGroup, AutocompleteItem } from "../autocomplete.types";
import { AutocompleteContext } from "./autocomplete.context";
import { type InputSize } from "../../../elements";

export type AutocompleteProviderProps<T> = {
  onChange?: Optional<(value?: Optional<AutocompleteItem<T>>) => void>;
  defaultOptions?: Optional<AutocompleteItem<T>[]>;
  variant?: Optional<"default" | "mobile">;
  value?: Optional<AutocompleteItem<T>>;
  onOpen?: Optional<() => void>;
  onClose?: Optional<() => void>;
  options: AutocompleteGroup<T>[];
  isOpen?: Optional<boolean>;
  children: ReactNode;
  label: string;
  dataQA?: string;
  dataQaExtra?: string;
  size?: InputSize;
};

function formatSlug(value: string): string {
  if (!value || typeof value !== "string") {
    return "";
  }

  return value
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

export function AutocompleteProvider<T>({
  isOpen: defaultIsOpen,
  defaultOptions,
  onChange,
  children,
  onClose,
  onOpen,
  options,
  variant,
  value,
  label,
  dataQA,
  dataQaExtra,
  size,
}: AutocompleteProviderProps<T>) {
  const [isOpen, setIsOpen] = useState<boolean>(!!defaultIsOpen);
  const [inputValue, setInputValue] = useState<string>("");

  function close() {
    if (onClose) {
      onClose();
    }

    setIsOpen(false);
  }

  const filteredOptions = useMemo(() => {
    return options
      .map((group) => {
        return {
          ...group,
          items:
            group?.items
              ?.filter((item) => {
                return formatSlug(item.label).includes(formatSlug(inputValue));
              })
              ?.slice(0, 5) ?? [],
        };
      })
      .filter((group) => group.items.length > 0);
  }, [inputValue, options]);

  const hasResults = useMemo(() => {
    return filteredOptions.length > 0;
  }, [filteredOptions]);

  const selectedItem = useMemo(() => {
    const [item] = options
      .flatMap((group) => {
        return group.items?.find((item) => item.value === value?.value);
      })
      .filter((item) => !!item);

    return item;
  }, [options, value]);

  useEffect(() => {
    if (!isOpen) {
      setInputValue(selectedItem?.label ?? "");
    }
  }, [selectedItem, isOpen]);

  useEffect(() => {
    setIsOpen(!!defaultIsOpen);
  }, [defaultIsOpen]);

  useEffect(() => {
    if (isOpen) {
      if (onOpen) {
        onOpen();
      }
    }
  }, [isOpen]);

  return (
    <AutocompleteContext.Provider
      value={{
        options,
        isOpen,
        inputValue,
        setInputValue,
        filteredOptions,
        hasResults,
        label,
        value,
        selectedItem,
        variant: variant ?? "default",
        defaultOptions: defaultOptions ?? [],
        close,
        onChange,
        setIsOpen,
        onClose,
        dataQA,
        dataQaExtra,
        size,
      }}
    >
      {children}
    </AutocompleteContext.Provider>
  );
}
